<template>
  <div
    id="product-form"
    class="product-form"
    :class="{ [$class('loading')]: loading }"
    data-sign-post-location="main"
    js-sign-post
  >
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <product-options
        v-show="!product.hasOnlyDefaultVariant"
        class="product-form__options"
        :loading="loading"
        :product="product"
        :section="section"
        :variant="variant"
        :display-missing-options="displayMissingOptions"
        :is-bis="isBisEnabled"
        :size-guide-position="sizeGuidePosition"
        @update-variant="handleUpdateVariant"
        @options-selected="handleOptionsSelected"
      >
        <template #sizeGuide>
          <slot name="sizeGuide" />
        </template>
      </product-options>

      <input
        name="quantity"
        readonly
        type="hidden"
        :value="quantity"
      >

      <product-addons
        v-if="section.enableAddOns"
        :product="product"
        @update:add-on-variant="payload => addon = payload"
      />

      <div
        v-if="messages"
        class="product-form__messages"
      >
        <p
          v-for="(message, key) of messages"
          :key="key"
          :class="{
            'product-form__error': key === 'final_sale' && !messages.made_to_order
          }"
          v-html="message"
        />
      </div>

      <!-- ATC -->
      <div
        v-if="!hideATC"
        class="product-form__actions"
      >
        <back-in-stock-btn
          v-if="isBisEnabled"
          :product="product"
          :variant="variant"
          @missing-variant="displayMissingOptions = true"
        />

        <btn
          v-else
          class="product-form__add-to-cart"
          :label="addToCartText"
          :show-state-icon="state === 'adding'"
          type="submit"
          :disabled="isOutOfStock"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>

        <wishlist-button
          :product="product"
          :variant="variant"
          :variant-rerender="wishlistVariantRerender"
        />
      </div>
    </form>

    <div
      v-if="showContact"
      class="product-form__contact-container"
      :class="hideATC ? 'mt-l' : 'mt-m'"
    >
      <btn
        type="button"
        js-gorgias="btn"
        data-type="drawer"
        data-direction="left"
        data-footer="true"
        data-uid="36rrbzoo"
        :label="$string('product.contact_form.contact_us')"
        :modifiers="['block', 'center' , hideATC ? 'primary' : 'secondary']"
      />

      <wishlist-button
        v-if="hideATC"
        :product="product"
        :variant="variant"
        :variant-rerender="wishlistVariantRerender"
      />
    </div>

    <div
      v-if="!braceletFinder && context !== 'drawer'"
      class="product-form__delivery-returns mt-m"
    >
      <div
        v-show="showEDD && narvarHydrated"
        class="text-body-s-regular product-form__edd"
      >
        <icon-delivery />
        <div
          ref="narvarTarget"
          data-narvar-feature="promiseEddCalculator"
        />
      </div>

      <div class="product-form__returns">
        <icon-returns />
        <span
          class="text-body-s-regular"
          v-text="$string('product.form.returns')"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'

import BackInStockBtn from '~async/back-in-stock/back-in-stock-btn'
import ProductAddons from '~async/product-addons/product-addons'
import ProductOptions from '~async/product-form/product-options'
import WishlistButton from '~async/wishlist-button/wishlist-button'

import IconLoading from '~icons/general/loading.svg'
import IconDelivery from '~icons/general/delivery.svg'
import IconReturns from '~icons/general/returns.svg'

import { emitUpdateVariant } from '~/helpers/vue'
import { getLineItemProperties, getMessagingByThemeString, getMessagingMetafields } from '~/helpers/product'
import { addToCartEvent, formatEcommerceEvent, track } from '~/helpers/datalayer-actions'
import { getMetafield } from '~/helpers/general'
import geolocation from '~stores/geolocation'

export default {
  name: 'ProductForm',

  components: {
    ProductAddons,
    BackInStockBtn,
    Btn,
    IconLoading,
    IconDelivery,
    IconReturns,
    ProductOptions,
    WishlistButton,
  },

  inject: {
    context: {
      default: 'PDP',
    },
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        resetFormBehaviour: 'disabled',
        enableAddOns: true,
        displayAddOnPrice: false,
      }),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    sizeGuidePosition: {
      type: String,
      default: 'right',
    },
    wishlistVariantRerender: {
      type: Boolean,
      default: true,
    },
    braceletFinder: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      addon: null,
      error: '',
      quantity: 1,
      selectedCountry: window.cnvs.store.country,
      state: 'ready',
      timeout: {},
      displayMissingOptions: false,
      displayRedFinalSale: true,
      narvarHydrated: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      quickAddUID: (state) => state['quick-add'].uid,
      geolocation: (state) => state.geolocation,
      cartCount: (state) => state.cart.response.item_count,
    }),

    /**
     * Compute the back in stock message
     * @returns {string}
     */
    bisMessage() {
      const bisDate = getMetafield({
        metaString: 'dates.back_in_stock',
        metafields: this.variant?.metafields || [],
      })

      if (!bisDate) {
        return ''
      }

      return this.$string('back_in_stock.product_form.message_html', {
        replace: {
          date: bisDate,
        },
      })
    },

    /**
     * Compute if variant is backordered
     * @returns {boolean}
     */
    isBackordered() {
      if (!this.variant) {
        return false
      }

      return Boolean(getMetafield({
        metafields: this.variant.metafields,
        metaString: 'product_details.back_order',
      }))
    },

    /**
     * Compute if variant is made to order
     * @returns {boolean}
     */
    isMadeToOrder() {
      if (!this.variant) {
        return false
      }

      return Boolean(getMetafield({
        metafields: this.variant.metafields,
        metaString: 'product_details.made_to_order',
      }))
    },

    /**
     * Compute if back in stock is enabled
     * @returns {Boolean}
     */
    isBisEnabled() {
      return this.$variable('settings.bis.enabled') &&
        this.isOutOfStock &&
        this.product?.tags?.includes('notify-bis')
    },

    /**
     * Compute if product or variant is out of stock
     * @returns {boolean}
     */
    isOutOfStock() {
      const context = this.variant || this.product
      return !context?.available
    },

    /**
     * Compute if product is a preorder product
     * @returns {boolean}
     */
    isPreorder() {
      if (!this.product?.tags?.length) {
        return false
      }
      return this.product.tags.includes('preorder')
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (this.isOutOfStock) {
        return this.$string('product.cta.sold_out')
      }

      if (!this.variant) {
        return this.isPreorder
          ? this.$string('product.cta.select_preorder')
          : this.$string('product.cta.select_variant')
      }

      let atc = this.isPreorder
        ? this.$string('product.cta.preorder')
        : this.$string('product.cta.add_to_cart')

      if (this.state === 'added') {
        atc = this.$string('product.cta.added_to_cart')
      } else if (this.addon?.variant && this.section.displayAddOnPrice) {
        const price = this.addon.variant.price.amount + this.variant.price.amount
        atc = `${this.$formatMoney(price)} - ${atc}`
      }

      return atc
    },

    /**
     * Compute product form messaging
     * @returns {{string: string}|null}
     */
    messages() {
      const messages = {}

      /**
       * Handle all messaging namespace metafields
       */
      const allMessages = getMessagingMetafields(this.product, this.variant)
      allMessages.forEach((meta) => {
        if (!meta.value) {
          return
        }
        messages[meta.key] = meta.value
      })

      if (!this.isPreorder) {
        delete messages.preorder
      }

      if (this.variant) {
        const metafields = this.$variable('metafields.cart.lineItem') || []
        metafields.forEach((metaStr) => {
          const msg = getMessagingByThemeString({
            object: this.variant,
            metaStr,
            keySuffix: '_pdp',
          })

          if (!msg || messages[msg.key]) {
            return
          }

          messages[msg.key] = msg.string
        })
      }

      if (!this.isBackordered) {
        delete messages.back_order
      }

      if (this.isBisEnabled && this.bisMessage) {
        messages.back_in_stock = this.bisMessage
      }

      /**
       * Handle pdp-message tags
       */
      const messageTags = this.product?.tags?.filter((tag) => tag.startsWith('pdp-message:')) || []
      messageTags.forEach((tag) => {
        messages[tag] = tag.replace('pdp-message:', '').trim()
      })

      return Object.keys(messages).length ? messages : null
    },

    /**
     * Compute if ATC is hidden
     * @returns {boolean}
     */
    hideATC() {
      return this.product?.tags.includes('hide-atc') && !this.braceletFinder
    },

    /**
     * Compute if client advisor CTA should be displayed
     * @returns {boolean}
     */
    showContact() {
      return this.product?.tags.includes('show-contact') && !this.braceletFinder
    },

    /**
     * Compute if estimated delivery date (EDD) should display
     * @returns {boolean}
     */
    showEDD() {
      return !this.isOutOfStock && !this.isBackordered && !this.hideATC && !this.isMadeToOrder && this.geolocation?.country === 'US'
    },
  },

  watch: {

    /**
     * Watch product to reset quantity and error.
     */
    product() {
      this.quantity = 1
      this.error = ''
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
    },
  },

  mounted() {
    this.initNarvarEdd()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.displayMissingOptions = true
        this.error = this.$string('product.form.select_variant')
        return
      }

      this.displayMissingOptions = false
      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (cnvs.storybook) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out')
          this.state = 'ready'
          return
        }

        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      const lineItemProperties = getLineItemProperties(this.variant, this.product)

      if (!this.isPreorder) {
        delete lineItemProperties.preorder
      }

      const items = [
        {
          id: this.variant.id,
          properties: {
            ...lineItemProperties,
            ...this.getRebuyProperties(),
          },
          quantity: this.quantity,
        },
      ]

      try {
        if (this.addon?.product) {
          items.push({
            id: this.addon.variant.id,
            quantity: 1,
            properties: getLineItemProperties(this.addon.variant, this.addon.product),
          })
        }

        await this.addItem(items)
        this.state = 'added'

        this.sendAnalytics()

        cnvs.EventBus.emit('main-product:add-to-cart', items)
        document.dispatchEvent(new CustomEvent('main-product:add-to-cart', { detail: items }))

        this.braceletFinderRedirect()

        if (
          this.section.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer') && !this.braceletFinder
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()
      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = error.description
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'))
      })

      this.state = 'ready'

      /**
       * Clear variant selection based on setting.
       */
      if (this.section.resetFormBehaviour !== 'clear') {
        return
      }

      this.handleUpdateVariant(false)
      this.quantity = 1
    },

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)
    },

    /**
     * Handle options selected.
     * @param {object} options
     */
    handleOptionsSelected(options) {
      track({
        event: `${this.context === 'drawer' ? 'quick_add_' : ''}options_selected`,
        options: Object.values(options),
      })
    },

    /**
     * Get additional line item properties for Rebuy from URL search params
     * @returns {{}}
     */
    getRebuyProperties() {
      const params = new URLSearchParams(window.location.search)
      const props = {}
      const keys = ['_source', '_attribution', '_widget_id']

      keys.forEach((key) => {
        if (params.has(key)) {
          props[key] = params.get(key)
        }
      })

      return props
    },

    /**
     * Send add to cart analytics
     */
    sendAnalytics() {
      addToCartEvent(this.variant, this.product)

      if (this.context !== 'drawer') {
        return
      }

      track({
        event: 'quick_add_add_to_cart',
        ...formatEcommerceEvent({ variant: this.variant, product: this.product }),
      })
    },

    /**
     * Initialize Narvar estimated delivery date (EDD) promise widget
     * @see https://support.narvar.com/hc/en-us/articles/32584523794195-Upgrade-Promise-on-Shopify#h_01J5VA10NY86FXSKNBEDVJ41N1
     */
    async initNarvarEdd() {
      const narvarTarget = this.$refs.narvarTarget
      if (
        this.selectedCountry !== 'US' ||
        this.braceletFinder ||
        !narvarTarget ||
        this.$variable('template.name') !== 'product' ||
        this.context === 'drawer'
      ) {
        return
      }

      await this.$nextTick()

      // Modify how the EDD widget looks
      const observer = new MutationObserver(
        (mutations) => this.handleEddModifications(mutations, observer),
      )
      observer.observe(narvarTarget, { subtree: true, childList: true })

      // Initialize EDD script
      const script = document.createElement('script')
      script.async = true
      script.src = 'https://js-prod01.narvar.com/johnhardy/promise_pdp.js'
      script.onload = () => {
        window.narvar('promiseEddCalculator', this.$variable('narvar.edd'))
      }
      document.head.append(script)
    },

    handleEddModifications(mutations, observer) {
      const addedNodes = [...mutations].find((mutation) => mutation.addedNodes.length)?.addedNodes || []

      if (!addedNodes.length) {
        return
      }

      // Modifications on first load
      if (!this.narvarHydrated) {
        this.onEddMount()
      } else {
        this.moveZipcodeForm()
      }

      // Pause observer to avoid endless loop as we update the delivery date text
      observer.disconnect()
      this.modifyEddText()

      this.narvarHydrated = true

      // Restart observer to update widget whenever it re-renders
      observer.observe(this.$refs.narvarTarget, { subtree: true, childList: true })
    },

    /**
     * Customize Narvar widget on first render
     */
    onEddMount() {
      const narvarElement = this.$refs.narvarTarget.children[0]

      // Replace the text "Ship to 12345" with "Expected delivery to 12345"
      for (let node of narvarElement.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue) {
          node.nodeValue = `${this.$string('product.form.narvar_promise')}`
          break
        }
      }

      // Add custom classes to zipcode to update styling and to modify the zipcode
      // on re-renders
      const button = narvarElement.querySelector('button')
      if (button) {
        button.classList.add('product-form__edd-zip', 'js-edd-zip')

        // Add colon after zipcode e.g. "Expected delivery to 12345:"
        button.insertAdjacentHTML('afterend', '<span>: </span>')
      }

      // Add custom classes to estimated delivery date, e.g. "Monday, January 1"
      // Used for updating styles and modifying the date on re-renders
      const date = narvarElement.querySelector('div:last-of-type')
      if (date) {
        date.classList.add('product-form__edd-date', 'js-edd-date')
      }
    },

    /**
     * Remove day from edd. e.g. "Monday, January 1" -> "January 1"
     */
    modifyEddText() {
      const narvarElement = this.$refs.narvarTarget.children[0]
      const date = narvarElement.querySelector('.js-edd-date > div')
      if (!date) {
        return
      }

      for (let node of date.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue) {
          node.nodeValue = `${node.nodeValue.split(',')[1] || node.nodeValue}`.trim()
          break
        }
      }
    },

    /**
     * Move the update zipcode form from the middle of the widget to the end
     * of the widget
     */
    moveZipcodeForm() {
      const narvarElement = this.$refs.narvarTarget.children[0]
      const form = narvarElement.querySelector('form')
      if (!form) {
        return
      }
      form.parentElement.classList.add('product-form__edd-form')
      narvarElement.appendChild(form.parentElement)
    },

    /**
     * Redirect to cart or checkout after adding to cart from bracelet finder
     */
    braceletFinderRedirect() {
      if (!this.braceletFinder) {
        return
      }
      if (this.cartCount === 1) {
        window.location = window.Shopify.routes.root + 'checkout'
      } else if (this.cartCount > 1) {
        window.location = this.$variable('routes.cart.url')
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
